import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  fetchX402EvidenceDiscovery,
  type X402EvidenceCandidate,
  type X402EvidenceDiscoveryResponse,
} from "../../lib/x402Evidence";

import { serviceBrand, serviceDescription, serviceName } from "../../lib/serviceBranding";
import { ServiceBrand } from "./ServiceBrand";

export function X402EvidenceFrontDoor({ initialQuery = "" }: { initialQuery?: string }) {
  const [query, setQuery] = useState(initialQuery);
  const [reviewer, setReviewer] = useState("");
  const [result, setResult] = useState<X402EvidenceDiscoveryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const discoveryRequestId = useRef(0);
  const discoveryAbort = useRef<AbortController | null>(null);

  async function loadDiscovery(nextQuery = query, nextReviewer = reviewer) {
    const requestId = ++discoveryRequestId.current;
    discoveryAbort.current?.abort();
    const controller = new AbortController();
    discoveryAbort.current = controller;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const next = await fetchX402EvidenceDiscovery({ q: nextQuery, reviewer: nextReviewer, limit: 10, signal: controller.signal });
      if (requestId === discoveryRequestId.current) setResult(next);
    } catch (cause) {
      if (requestId === discoveryRequestId.current && !controller.signal.aborted) {
        setResult(null);
        setError(cause instanceof Error ? cause.message : "Boon could not load free discovery.");
      }
    } finally {
      if (requestId === discoveryRequestId.current) setLoading(false);
    }
  }

  useEffect(() => {
    setQuery(initialQuery);
    setReviewer("");
    void loadDiscovery(initialQuery, "");
    return () => {
      discoveryRequestId.current += 1;
      discoveryAbort.current?.abort();
    };
  }, [initialQuery]);

  function submit(event: FormEvent) {
    event.preventDefault();
    void loadDiscovery();
  }

  return (
    <>
      <main className="overflow-x-clip">
        <section className="px-6 pt-8 md:px-10 md:pt-14 max-w-[1440px] mx-auto">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-start">
            <header className="max-w-4xl animate-fade-up" data-discovery-intro="true">
              <p className="text-sm text-olive-deep btn-mono tracking-wide uppercase">free service discovery</p>
              <h1 className="mt-3 text-4xl font-display tracking-tight leading-[1.02] md:text-6xl">Explore x402 services.</h1>
              <p className="mt-5 max-w-3xl text-base text-ink-soft leading-relaxed md:text-lg">
                See who used a service, what they said, and when. Read the evidence before deciding what your agent should try.
              </p>
            </header>

            <form className="rounded-xl border border-faint bg-paper-deep/35 p-4 shadow-card md:p-5" onSubmit={submit} data-discovery-search="true" aria-label="Free service discovery search">
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                <label className="block min-w-0 sm:col-span-2">
                  <span className="text-sm font-semibold text-ink">What should the service help with?</span>
                  <input
                    className="mt-2 min-h-12 w-full rounded-lg border border-faint bg-paper px-4 py-3 text-base focus:border-olive focus:outline-none focus:ring-2 focus:ring-olive/25"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search, research, images, data…"
                    aria-label="Discovery task"
                  />
                </label>
                <label className="block min-w-0">
                  <span className="text-sm font-semibold text-ink">Reviewer wallet <span className="font-normal text-muted">(optional)</span></span>
                  <input
                    className="mt-2 min-h-12 w-full rounded-lg border border-faint bg-paper px-4 py-3 font-mono text-sm focus:border-olive focus:outline-none focus:ring-2 focus:ring-olive/25"
                    value={reviewer}
                    onChange={(event) => setReviewer(event.target.value)}
                    placeholder="0x…"
                    inputMode="text"
                    aria-label="Reviewer wallet address"
                  />
                </label>
                <button className="btn btn-primary min-h-12 w-full md:w-auto" type="submit" disabled={loading}>
                  {loading ? "Searching…" : "Search free"}
                </button>
              </div>
              <p className="mt-3 text-xs text-muted">This search reads evidence only. It does not open an endpoint, spend money, sign a wallet, or publish a review.</p>
            </form>
          </div>
        </section>

        <section className="px-6 mt-10 md:px-10 md:mt-14 max-w-[1440px] mx-auto" aria-labelledby="evidence-results-heading">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 id="evidence-results-heading" className="text-2xl font-display tracking-tight md:text-3xl">Services</h2>
              <p className="mt-2 text-sm text-muted">Reviews are opinions. Payment and recognition do not verify service quality.</p>
            </div>
            {result && <p className="btn-mono text-xs text-muted">Snapshot assembled {formatDate(result.generatedAt)}</p>}
          </div>

          {loading && <p className="mt-8 rounded-xl border border-faint bg-paper p-6 text-sm text-muted" role="status">Reading public discovery evidence…</p>}
          {error && <p className="mt-8 rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800" role="alert">{error}</p>}
          {!loading && !error && result && result.candidates.length === 0 && (
            <div className="mt-8 rounded-xl border border-faint bg-paper p-6">
              <p className="font-display text-xl">No matching services yet.</p>
              <p className="mt-2 text-sm text-muted leading-relaxed">Try a broader task or leave the reviewer wallet blank. Missing data is not a quality verdict.</p>
            </div>
          )}
          {!loading && !error && result && result.candidates.length > 0 && (
            <div className="mt-6 grid min-w-0 gap-4 xl:grid-cols-2">
              {result.candidates.map((candidate) => (
                <EvidenceCandidateCard key={candidate.routeId} candidate={candidate} />
              ))}
            </div>
          )}

          <p className="mt-7 text-xs text-muted">Provider names and icons from <a className="text-olive-deep hover:underline" href="https://www.x402scan.com/" target="_blank" rel="noreferrer">x402scan</a> when available.</p>
        </section>
      </main>
    </>
  );
}

export function EvidenceCandidateCard({ candidate }: { candidate: X402EvidenceCandidate }) {
  const summary = candidateSummary(candidate);
  return (
    <article className="min-w-0 overflow-hidden rounded-xl border border-faint bg-paper p-5 shadow-card md:p-6">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <ServiceBrand endpoint={candidate.url} compact />
          <h3 className="mt-3 text-2xl font-display tracking-tight break-words">{candidateTitle(candidate)}</h3>
        </div>
        <a className="shrink-0 btn-mono text-xs text-olive-deep hover:underline" href={candidate.links.web}>Open page ↗</a>
      </div>
      {summary && <p className="mt-4 text-sm text-ink-soft leading-relaxed">{summary}</p>}
      <div className="mt-5 grid gap-3 border-y border-faint py-4 sm:grid-cols-2">
        <EvidenceFact label="Use facts in this snapshot" value={formatUsage(candidate.usage)} />
        <EvidenceFact label="Price status" value={formatPrice(candidate.price)} />
      </div>
      {candidate.reviews.length > 0 ? (
        <div className="mt-5 space-y-3">
          <p className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">Reviewer text</p>
          {candidate.reviews.map((review, index) => (
            <blockquote key={`${review.publishedAt}-${index}`} className="border-l-2 border-olive/50 pl-3 text-base leading-relaxed text-ink">
              <p>“{review.text}”</p>
              <footer className="mt-1 text-xs text-muted">{humanEvidenceLabel(review.evidenceKind)} · Reviewed by {review.reviewer ? shortAddress(review.reviewer) : "identity not linked in this snapshot"} · {formatDate(review.publishedAt)}{safeSourceUrl(review.sourceUrl) && <>{" · "}<a className="text-olive-deep hover:underline" href={review.sourceUrl}>source ↗</a></>}</footer>
            </blockquote>
          ))}
        </div>
      ) : <p className="mt-5 text-sm text-muted">No public reviewer text is available for this candidate yet.</p>}
    </article>
  );
}

function EvidenceFact({ label, value }: { label: string; value: string }) {
  return <div><p className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">{label}</p><p className="mt-1 text-sm text-ink break-words">{value}</p></div>;
}

function candidateTitle(candidate: X402EvidenceCandidate): string {
  const description = serviceDescription(candidate.url, candidate.method);
  if (description) return serviceName(candidate.url, candidate.method, description);
  const fallback = candidate.title.trim();
  if (fallback && !/^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b|https?:\/\//i.test(fallback)) return fallback;
  return serviceBrand(candidate.url).name;
}

function candidateSummary(candidate: X402EvidenceCandidate): string | null {
  const title = candidateTitle(candidate);
  const description = serviceDescription(candidate.url, candidate.method);
  const prefix = `${title} - `;
  const summary = (description?.startsWith(prefix)
    ? description.slice(prefix.length)
    : description ?? candidate.summary).trim();
  const normalize = (text: string) => text.trim().replace(/\s+/g, " ").toLowerCase();
  return summary && normalize(summary) !== normalize(title) ? summary : null;
}

function humanEvidenceLabel(kind: string): string {
  if (kind === "boon_backed") return "Boon-backed review";
  if (kind === "receipt_verified") return "Receipt-verified use";
  if (kind === "client_settlement") return "Payment-linked review";
  return "Self-reported review";
}

function formatUsage(usage: X402EvidenceCandidate["usage"]): string {
  if (usage.useCount === 0 && usage.walletCount === 0 && usage.profileCount === 0) {
    return "No use facts observed in this snapshot";
  }
  return `${usage.useCount} ${usage.useCount === 1 ? "use" : "uses"} · ${usage.walletCount} ${usage.walletCount === 1 ? "wallet" : "wallets"} · ${usage.profileCount} ${usage.profileCount === 1 ? "profile" : "profiles"}`;
}

function safeSourceUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.origin === "https://api.boonprotocol.com" && url.pathname.startsWith("/api/site/x402/");
  } catch {
    return false;
  }
}

function formatPrice(price: X402EvidenceCandidate["price"]): string {
  if (price.status === "stale") return `stale${price.amount && price.currency ? ` · ${price.amount} ${price.currency}` : ""}`;
  if (price.amount === null || price.currency === null) return "Unknown price";
  return `${price.amount} ${price.currency}${price.checkedAt ? ` · recorded ${formatDate(price.checkedAt)}` : ""}`;
}

function formatDate(value: string | null): string {
  if (!value) return "date unavailable";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "date unavailable" : parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function shortAddress(value: string): string {
  return value.length > 12 ? `${value.slice(0, 6)}…${value.slice(-4)}` : value;
}
